import { useLanguage } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Lock, ShieldCheck, CheckCircle } from "lucide-react";
import { useRecaptchaToken } from "../hooks/useRecaptchaToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// === Carousel A: GED leaders (scroll left) ===
const gedItems = [
  { id: "yooz", name: "Yooz", logo: "https://customer-assets.emergentagent.com/job_5577aad2-a1d8-4595-9afc-d525b5b1b883/artifacts/6og8kqyj_Yooz-Logo-Full-Colors-Transparent%20%283%29.png", href: "/solution/yooz" },
  { id: "zeendoc", name: "Zeendoc", logo: "https://customer-assets.emergentagent.com/job_4f3dc9ba-8fc8-4fff-96ff-60223c7a8223/artifacts/cskigm18_2f90b336-437c-4703-9aaa-a858df680c7a.png", href: "/solution/zeendoc" },
  { id: "docuware", name: "DocuWare", logo: "https://customer-assets.emergentagent.com/job_4f3dc9ba-8fc8-4fff-96ff-60223c7a8223/artifacts/yrn5at9h_logo%20docuware.GIF", href: "/solution/docuware" },
  { id: "neoledge", name: "NeoLedge", logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/pcnw3uxp_logo%20neoledge%201.0.GIF", href: "/solution/neoledge" },
  { id: "twind", name: "Twind", logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/fjy58uyr_logo%20twind.GIF", href: "/solution/twind" }
];

// === Carousel B: Integrations (scroll right / reverse) ===
const integrationItems = [
  { id: "hubspot", name: "HubSpot", logo: "https://customer-assets.emergentagent.com/job_5577aad2-a1d8-4595-9afc-d525b5b1b883/artifacts/wkc957jr_HubSpot_Logo.svg.png" },
  { id: "sap", name: "SAP", logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/t06x5a8u_Design%20sans%20titre.png" },
  { id: "odoo", name: "Odoo", logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/ydv982wv_odoo_logo.png" },
  { id: "sage", name: "Sage", logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/0nl2vhn4_Sage_logo.svg.png" },
  { id: "ovh", name: "OVHcloud", logo: "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/1metw1w7_Logo_OVH.svg.png" }
];

const labels = {
  fr: {
    ged: "Solutions GED recommandées",
    integ: "Intégrations & Partenaires",
    vendorText: "Vous voulez apparaître ici ?",
    vendorLink: "Demander une visibilité sponsorisée",
    modal: {
      title: "Demande d'accès Sponsor",
      sub: "Notre équipe valide chaque demande afin de garantir la qualité des partenaires visibles sur Documatch.",
      company: "Société",
      fullName: "Nom complet",
      email: "Email professionnel",
      website: "Site web",
      erp: "ERP/GED utilisé",
      erpOptional: "(optionnel)",
      message: "Message",
      submit: "Demander un accès sponsor",
      sending: "Envoi...",
      successTitle: "Demande envoyée avec succès",
      successDesc: "Notre équipe revient vers vous sous 24 heures ouvrées après validation manuelle.",
      close: "Fermer",
      reassure: "Validation manuelle · Réponse sous 24h · Programme exclusif"
    }
  },
  es: {
    ged: "Soluciones GED recomendadas",
    integ: "Integraciones y Partners",
    vendorText: "¿Quieres aparecer aquí?",
    vendorLink: "Solicitar visibilidad patrocinada",
    modal: {
      title: "Solicitud de acceso Sponsor",
      sub: "Nuestro equipo valida cada solicitud para garantizar la calidad de los partners visibles en Documatch.",
      company: "Empresa",
      fullName: "Nombre completo",
      email: "Email profesional",
      website: "Sitio web",
      erp: "ERP/DMS utilizado",
      erpOptional: "(opcional)",
      message: "Mensaje",
      submit: "Solicitar acceso sponsor",
      sending: "Enviando...",
      successTitle: "Solicitud enviada con éxito",
      successDesc: "Nuestro equipo te contactará en 24 horas laborables tras validación manual.",
      close: "Cerrar",
      reassure: "Validación manual · Respuesta en 24h · Programa exclusivo"
    }
  },
  en: {
    ged: "Recommended DMS Solutions",
    integ: "Integrations & Partners",
    vendorText: "Want to appear here?",
    vendorLink: "Request sponsored visibility",
    modal: {
      title: "Sponsor access request",
      sub: "Our team manually validates each request to ensure the quality of partners visible on Documatch.",
      company: "Company",
      fullName: "Full name",
      email: "Professional email",
      website: "Website",
      erp: "ERP/DMS used",
      erpOptional: "(optional)",
      message: "Message",
      submit: "Request sponsor access",
      sending: "Sending...",
      successTitle: "Request sent successfully",
      successDesc: "Our team will get back to you within 24 working hours after manual validation.",
      close: "Close",
      reassure: "Manual validation · 24h response · Exclusive program"
    }
  }
};

const LogoTile = ({ item }) => {
  const content = item.logo ? (
    <img src={item.logo} alt={item.name} className="max-h-12 max-w-[140px] w-auto h-auto object-contain" />
  ) : (
    <div
      className="h-12 px-5 flex items-center justify-center rounded-lg font-bold text-white text-sm md:text-base tracking-wide"
      style={{ backgroundColor: item.bg }}
    >
      {item.text}
    </div>
  );

  const baseClass = "flex-shrink-0 mx-8 grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300 h-16 w-[160px] flex items-center justify-center";

  if (item.href) {
    return (
      <a href={item.href} className={`${baseClass} cursor-pointer`} data-testid={`carousel-item-${item.id}`} title={item.name}>
        {content}
      </a>
    );
  }
  return (
    <div className={baseClass} data-testid={`carousel-item-${item.id}`} title={item.name}>
      {content}
    </div>
  );
};

export const Sponsors = () => {
  const { language } = useLanguage();
  const l = labels[language] || labels.fr;
  const m = l.modal;
  const [modalOpen, setModalOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ company: "", fullName: "", email: "", website: "", erp: "", message: "" });
  const getRecaptchaToken = useRecaptchaToken();

  // Auto-open modal when arriving with #sponsor-request hash (from gated /sponsors page)
  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === "#sponsor-request") {
        setModalOpen(true);
      }
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.fullName || !form.email) return;
    setSending(true);
    try {
      const recaptchaToken = await getRecaptchaToken("sponsor_request");
      await axios.post(`${API}/sponsor-request`, { ...form, language, recaptchaToken });
      setSent(true);
    } catch (err) {
      console.error("Sponsor request error:", err?.response?.data || err);
      toast.error(language === "fr" ? "Erreur lors de l'envoi" : language === "es" ? "Error al enviar" : "Error sending request");
    } finally {
      setSending(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setSent(false);
      setForm({ company: "", fullName: "", email: "", website: "", erp: "", message: "" });
    }, 300);
  };

  // Triplicate for infinite loop
  const gedLoop = [...gedItems, ...gedItems, ...gedItems];
  const integLoop = [...integrationItems, ...integrationItems, ...integrationItems];

  return (
    <div className="max-w-7xl mx-auto space-y-10" data-testid="sponsors-section">
      {/* === Carousel A: GED === */}
      <div>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-sm font-semibold text-slate-500 uppercase tracking-wider mb-6"
        >
          {l.ged}
        </motion.p>
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          <div className="flex items-center animate-scroll-left">
            {gedLoop.map((item, idx) => (
              <LogoTile key={`ged-${item.id}-${idx}`} item={item} />
            ))}
          </div>
        </div>
      </div>

      {/* === Carousel B: Integrations (reverse) === */}
      <div>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-sm font-semibold text-slate-500 uppercase tracking-wider mb-6"
        >
          {l.integ}
        </motion.p>
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          <div className="flex items-center animate-scroll-right">
            {integLoop.map((item, idx) => (
              <LogoTile key={`int-${item.id}-${idx}`} item={item} />
            ))}
          </div>
        </div>
      </div>

      {/* Vendor CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 transition-colors border border-dashed border-slate-300 hover:border-blue-400 px-4 py-2 rounded-lg"
          data-testid="vendor-cta"
        >
          <span>{l.vendorText}</span>
          <span className="font-semibold text-blue-600">{l.vendorLink}</span>
        </button>
      </motion.div>

      {/* Sponsor Access Request Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
            data-testid="sponsor-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white px-6 py-5 rounded-t-2xl relative">
                <button onClick={closeModal} className="absolute top-4 right-4 text-white/70 hover:text-white" aria-label="Close">
                  <X size={20} />
                </button>
                <div className="flex items-center gap-2 mb-1 text-xs font-semibold text-blue-100 uppercase tracking-wider">
                  <Lock size={12} /> Documatch Sponsor Program
                </div>
                <h2 className="text-xl font-bold">{m.title}</h2>
                <p className="text-sm text-blue-100 mt-1.5">{m.sub}</p>
              </div>

              {sent ? (
                /* Success state */
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-6 py-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-testid="sponsor-success">{m.successTitle}</h3>
                  <p className="text-sm text-slate-600 mb-6">{m.successDesc}</p>
                  <button onClick={closeModal} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold text-sm">
                    {m.close}
                  </button>
                </motion.div>
              ) : (
                /* Form */
                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-3.5">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">{m.company} <span className="text-red-500">*</span></label>
                      <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" data-testid="sponsor-modal-company" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">{m.fullName} <span className="text-red-500">*</span></label>
                      <input value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" data-testid="sponsor-modal-name" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{m.email} <span className="text-red-500">*</span></label>
                    <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" data-testid="sponsor-modal-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{m.website}</label>
                    <input type="url" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" data-testid="sponsor-modal-website" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{m.erp} <span className="text-slate-400 font-normal">{m.erpOptional}</span></label>
                    <input value={form.erp} onChange={e => setForm(p => ({ ...p, erp: e.target.value }))} placeholder="Sage, SAP, Odoo, Zeendoc..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" data-testid="sponsor-modal-erp" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{m.message}</label>
                    <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none" data-testid="sponsor-modal-message" />
                  </div>
                  <button
                    type="submit"
                    disabled={sending || !form.company || !form.fullName || !form.email}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                    data-testid="sponsor-modal-submit"
                  >
                    {sending ? <><Loader2 size={16} className="animate-spin" /> {m.sending}</> : m.submit}
                  </button>
                  <div className="flex items-center justify-center gap-1.5 pt-1 text-xs text-slate-500">
                    <ShieldCheck size={12} className="text-green-600" />
                    {m.reassure}
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Sponsors;
